import classNames from 'classnames';
import { format, getISOWeekYear } from 'date-fns';
import { convertToFormattedDate } from 'utils/date';
import { DayColumnHeader } from '../../components/headers/DayColumnHeader';
import { WeekColumnHeader } from '../../components/headers/WeekColumnHeader';
import { DEFAULT_COLUMN_SETTINGS } from '../../const';

import type {
  GridColDef,
  GridColumnGroupingModel,
  GridColumnNode,
  GridRenderCellParams,
  GridValidRowModel
} from '@mui/x-data-grid-pro';
import type { ReactNode } from 'react';
import type { RosterDay } from '../../types';

export type ColumnBuilder<R extends GridValidRowModel = GridValidRowModel> =
  (params: {
    rosterDays: RosterDay[];
    weekLabel: string;
    renderTimelineCell?: (params: GridRenderCellParams<R>) => ReactNode;
    columnWidth?: number;
    stretchTimelineColumns?: boolean;
  }) => {
    timeLineColumns: GridColDef<R>[];
    columnGroupingModel: GridColumnGroupingModel;
  };

const formatDate = (date: Date): string => format(date, 'dd-MM');

const getMondayOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
};

export const groupRosterDaysByWeek = (
  rosterDays: RosterDay[]
): RosterDay[][] => {
  const daysByWeekMap = new Map<string, RosterDay[]>();
  for (const rd of rosterDays) {
    // Key on the ISO week-year, not the calendar year: an ISO week that straddles New Year (e.g. Mon
    // 29 Dec – Sun 4 Jan) shares one weekNr but two calendar years, so `getFullYear()` would split it
    // into two columns, one of them permanently blank. `getISOWeekYear` gives both days the same year.
    const key = `${rd.weekNr}-${getISOWeekYear(rd.date)}`;
    const groupWeekDays = daysByWeekMap.get(key);
    if (groupWeekDays) {
      groupWeekDays.push(rd);
    } else {
      daysByWeekMap.set(key, [rd]);
    }
  }
  return [...daysByWeekMap.values()];
};

export const getWeekFieldName = (weekNr: number, year: number): string =>
  `week-${weekNr}-${year}`;

/**
 * A timeline column is either a fixed width, or a share of whatever the fixed columns leave over.
 *
 * Stretching keeps columnWidth as the floor rather than dropping it, so a timeline with more weeks than
 * fit still scrolls horizontally instead of squeezing them unreadably narrow. It also removes the
 * filler element the grid inserts when its columns do not add up to the viewport width — which, with a
 * pinned column on the right, otherwise shows up as blank space between the timeline and that column.
 */
const timelineColumnSizing = (columnWidth: number, stretch?: boolean) =>
  stretch ? { flex: 1, minWidth: columnWidth } : { width: columnWidth };

export const buildColumnWeeks = <
  R extends GridValidRowModel = GridValidRowModel
>({
  rosterDays,
  weekLabel,
  renderTimelineCell,
  columnWidth = 200,
  stretchTimelineColumns
}: {
  rosterDays: RosterDay[];
  weekLabel: string;
  renderTimelineCell?: (params: GridRenderCellParams<R>) => ReactNode;
  columnWidth?: number;
  stretchTimelineColumns?: boolean;
}): {
  timeLineColumns: GridColDef<R>[];
  columnGroupingModel: GridColumnGroupingModel;
} => {
  const weeks = groupRosterDaysByWeek(rosterDays);

  const timeLineColumns: GridColDef[] = weeks.map((week) => {
    const weekNr = week[0].weekNr;
    const fullYear = week[0].date.getFullYear();
    const isCurrentWeek = week.some((rd) => rd.isToday);
    const headerLabel = `${weekLabel} ${weekNr}`;
    const dateRange = formatDate(getMondayOfWeek(week[0].date));

    return {
      ...DEFAULT_COLUMN_SETTINGS,
      ...(renderTimelineCell ? { renderCell: renderTimelineCell } : {}),
      field: getWeekFieldName(weekNr, fullYear),
      headerName: headerLabel,
      renderHeader: () =>
        WeekColumnHeader({ weekLabel: headerLabel, dateRange }),
      headerClassName: isCurrentWeek ? 'week-col--current' : '',
      colSpan: (value) => (value?.cellColSpan ? value.cellColSpan : 1),
      ...timelineColumnSizing(columnWidth, stretchTimelineColumns)
    };
  });

  return { timeLineColumns, columnGroupingModel: [] };
};

export const buildColumnWeeksRelative = <
  R extends GridValidRowModel = GridValidRowModel
>({
  rosterDays,
  weekLabel,
  renderTimelineCell,
  columnWidth = 200,
  startDate,
  stretchTimelineColumns
}: {
  rosterDays: RosterDay[];
  weekLabel: string;
  renderTimelineCell?: (params: GridRenderCellParams<R>) => ReactNode;
  columnWidth?: number;
  startDate: Date;
  stretchTimelineColumns?: boolean;
}): {
  timeLineColumns: GridColDef<R>[];
  columnGroupingModel: GridColumnGroupingModel;
} => {
  const weeks = groupRosterDaysByWeek(rosterDays);

  // Find the week that contains startDate — that week becomes relative week 1
  const startWeekIndex = weeks.findIndex((week) => {
    const weekStart = week[0].date;
    const weekEnd = week[week.length - 1].date;
    return startDate >= weekStart && startDate <= weekEnd;
  });

  // Fall back to 0 if startDate falls before all rosterDays
  const baseIndex = startWeekIndex !== -1 ? startWeekIndex : 0;

  const timeLineColumns: GridColDef[] = weeks.map((week, index) => {
    const weekNr = week[0].weekNr;
    const fullYear = week[0].date.getFullYear();
    const isCurrentWeek = week.some((rd) => rd.isToday);
    const relativeWeekNumber = index - baseIndex + 1;
    const headerLabel = `${weekLabel} ${relativeWeekNumber}`;
    const dateRange = formatDate(getMondayOfWeek(week[0].date));

    return {
      ...DEFAULT_COLUMN_SETTINGS,
      ...(renderTimelineCell ? { renderCell: renderTimelineCell } : {}),
      field: getWeekFieldName(weekNr, fullYear),
      headerName: headerLabel,
      renderHeader: () =>
        WeekColumnHeader({ weekLabel: headerLabel, dateRange }),
      headerClassName: isCurrentWeek ? 'week-col--current' : '',
      colSpan: (value) => (value?.cellColSpan ? value.cellColSpan : 1),
      ...timelineColumnSizing(columnWidth, stretchTimelineColumns)
    };
  });

  return { timeLineColumns, columnGroupingModel: [] };
};

export const buildColumnDaysGroupedByWeek = <
  R extends GridValidRowModel = GridValidRowModel
>({
  rosterDays,
  weekLabel,
  renderTimelineCell
}: {
  rosterDays: RosterDay[];
  weekLabel: string;
  renderTimelineCell?: (params: GridRenderCellParams<R>) => ReactNode;
}): {
  timeLineColumns: GridColDef<R>[];
  columnGroupingModel: GridColumnGroupingModel;
} => {
  const timeLineColumns: GridColDef[] = rosterDays.map((rd) => ({
    ...DEFAULT_COLUMN_SETTINGS,
    ...(renderTimelineCell ? { renderCell: renderTimelineCell } : {}),
    field: convertToFormattedDate(rd.date),
    headerName: `${rd.dayName} ${rd.day}`,
    align: 'center',
    width: 70,
    renderHeader: () => DayColumnHeader({ ...rd }),
    headerClassName: classNames({
      'day-header--today': rd.isToday,
      'day-col--today': rd.isToday,
      'day-col--holiday': rd.isHoliday,
      'day-col--weekend': rd.dayNr === 0 || rd.dayNr === 6,
      'day-col--sunday': rd.dayNr === 0
    }),
    cellClassName: classNames({
      'day-col--today': rd.isToday,
      'day-col--holiday': rd.isHoliday,
      'day-col--weekend': rd.dayNr === 0 || rd.dayNr === 6,
      'day-col--sunday': rd.dayNr === 0
    })
  }));

  const weeks = groupRosterDaysByWeek(rosterDays);

  const columnGroupingModel: GridColumnGroupingModel = weeks.map((week) => {
    const weekNr = week[0].weekNr;
    const isCurrentWeek = week.some((rd) => rd.isToday);

    return {
      groupId: `week-${weekNr}`,
      headerName: `${weekLabel} ${weekNr}`,
      headerClassName: isCurrentWeek ? 'week-col--current' : '',
      headerAlign: 'center',
      children: week.map(
        (rd): GridColumnNode => ({
          field: convertToFormattedDate(rd.date)
        })
      )
    };
  });

  return { timeLineColumns, columnGroupingModel };
};
